import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics, categoryLabels } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const comic = comics.find(c => c.id === id);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-800 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-1 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors"
            >
              <i className="ri-home-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const catLabel = categoryLabels[comic.category] || '漫画';
  const relatedComics = comics
    .filter(c => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: catLabel, href: `/category/${comic.category}` },
    { label: comic.title },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': '首页',
        'item': 'https://pipicomics.com',
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'name': catLabel,
        'item': `https://pipicomics.com/category/${comic.category}`,
      },
      {
        '@type': 'ListItem',
        'position': 3,
        'name': comic.title,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': `皮皮漫画${comic.title}`,
    'description': comic.description,
    'author': {
      '@type': 'Person',
      'name': comic.author,
    },
    'datePublished': '2024-01-01',
    'dateModified': comic.updateTime,
    'image': comic.coverImage,
    'inLanguage': 'zh-CN',
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <Navbar />

      <main className="pt-20">
        <article className="py-8 md:py-10 px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-[300px] shrink-0">
                <div className="rounded-lg overflow-hidden bg-background-100 aspect-[2/3]">
                  <img
                    src={comic.coverImage}
                    alt={`皮皮漫画${comic.title}`}
                    title={`皮皮漫画${comic.title}`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  皮皮漫画{comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-sm text-foreground-600">作者：{comic.author}</span>
                  <span className="text-sm text-accent-500 font-semibold">★ {comic.rating}</span>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    comic.status === '连载中'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-secondary-200 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="text-xs text-foreground-500">
                    共 {comic.totalChapters} 话
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-5">
                  {comic.tags.map(tag => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs bg-background-100 text-foreground-700 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                  <span className="px-3 py-1 text-xs bg-accent-100 text-accent-700 rounded-full">
                    {catLabel}
                  </span>
                </div>

                <div className="bg-background-100 rounded-lg p-5 mb-6">
                  <h2 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {comic.description}
                  </p>
                </div>

                <div className="flex items-center gap-3 mb-6">
                  <button className="flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-line"></i> 从第一话开始阅读
                  </button>
                  <button className="flex items-center gap-2 px-6 py-3 bg-background-100 text-foreground-700 text-sm font-medium rounded-full hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-heart-line"></i> 收藏
                  </button>
                  <button className="flex items-center gap-2 px-6 py-3 bg-background-100 text-foreground-700 text-sm font-medium rounded-full hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-share-forward-line"></i> 分享
                  </button>
                </div>
              </div>
            </div>

            <section className="mt-10">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-1 h-5 bg-primary-500 rounded-full"></div>
                <h2 className="text-lg font-heading font-bold text-foreground-900">
                  章节列表
                </h2>
                <span className="text-sm text-foreground-500">
                  （共 {comic.totalChapters} 话）
                </span>
              </div>

              <div className="bg-background-100 rounded-lg divide-y divide-background-200">
                {comic.chapters.map((chapter, idx) => (
                  <div
                    key={chapter.id}
                    className="flex items-center justify-between px-5 py-4 hover:bg-background-200/50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xs text-foreground-500 w-12 shrink-0">
                        第 {chapter.number} 话
                      </span>
                      <span className="text-sm text-foreground-800 truncate">
                        {chapter.title}
                      </span>
                      {idx === 0 && (
                        <span className="px-2 py-0.5 text-[10px] bg-primary-100 text-primary-700 rounded-full shrink-0">
                          最新
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-foreground-500 shrink-0 ml-3">
                      {chapter.updateTime}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            {relatedComics.length > 0 && (
              <section className="mt-14">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-1 h-5 bg-accent-500 rounded-full"></div>
                  <h2 className="text-lg font-heading font-bold text-foreground-900">
                    更多{catLabel}作品
                  </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-5" data-product-shop>
                  {relatedComics.map(c => (
                    <ComicCard key={c.id} comic={c} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}